/*
 * Isolates a chat-history row's nested controls so title editing and deletion
 * cannot accidentally activate the row-level session resume behavior.
 */
import React, { useRef } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import { useTranslation } from 'src/arb-wrapper';
import {
  getSessionTitle,
  SESSION_TITLE_MAX_LENGTH,
  type ChatSession,
} from './chatHistoryView.utils';

interface ChatHistoryRowProps {
  session: ChatSession;
  campaignLabel: string | undefined;
  formattedDate: string | undefined;
  isEditing: boolean;
  titleDraft: string;
  editErrorMessageKey: string | undefined;
  hasDeleteError: boolean;
  onCancelEdit: () => void;
  onDelete: () => Promise<void>;
  onEdit: () => void;
  onResume: () => void;
  onSaveEdit: () => Promise<void>;
  onTitleChange: (title: string) => void;
}

export const ChatHistoryRow: React.FC<ChatHistoryRowProps> = ({
  session,
  campaignLabel,
  formattedDate,
  isEditing,
  titleDraft,
  editErrorMessageKey,
  hasDeleteError,
  onCancelEdit,
  onDelete,
  onEdit,
  onResume,
  onSaveEdit,
  onTitleChange,
}) => {
  const { t } = useTranslation();
  const interactionStartedWhileEditing = useRef(false);

  const handleRowClick = () => {
    const shouldIgnoreClick = isEditing || interactionStartedWhileEditing.current;
    interactionStartedWhileEditing.current = false;
    if (!shouldIgnoreClick) onResume();
  };

  return (
    <div
      aria-label={getSessionTitle(session)}
      data-testid={`chat-history-row-${session.sessionId}`}
      role="button"
      style={{ borderBottom: '1px solid #eaeded', cursor: 'pointer', padding: '12px 4px' }}
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget || isEditing) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onResume();
        }
      }}
      onMouseDown={() => { interactionStartedWhileEditing.current = isEditing; }}
    >
      <Row alignmentHorizontal="justify" alignmentVertical="center" spacing="200" width="100%">
        <Column spacing="100" width="100%">
          {isEditing
            ? (
            <Input
              aria-label={t('chat_history_edit')}
              data-testid={`chat-history-title-input-${session.sessionId}`}
              error={editErrorMessageKey !== undefined}
              inaccessibleAutoFocus={true}
              size="small"
              value={titleDraft}
              width="100%"
              onBlur={() => { void onSaveEdit(); }}
              onChange={onTitleChange}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  onCancelEdit();
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  void onSaveEdit();
                }
              }}
            />
              )
            : <Text breakWord={true} type="b300">{getSessionTitle(session)}</Text>}
          {campaignLabel && <Text color="secondary" type="b200">{campaignLabel}</Text>}
          {formattedDate && (
            <Text color="secondary" data-testid={`chat-history-date-${session.sessionId}`} type="b200">
              {formattedDate}
            </Text>
          )}
        </Column>
        <Row spacing="100">
          <Tooltip position="bottom" title={t('chat_history_edit')}>
            <Button
              aria-label={t('chat_history_edit')}
              data-testid={`chat-history-edit-${session.sessionId}`}
              propagateClickEvent={false}
              size="small"
              type="icon"
              onClick={onEdit}
            >
              <Icon tokens={editTokens} />
            </Button>
          </Tooltip>
          <Tooltip position="bottom" title={t('chat_history_delete')}>
            <Button
              aria-label={t('chat_history_delete')}
              data-testid={`chat-history-delete-${session.sessionId}`}
              propagateClickEvent={false}
              size="small"
              type="icon"
              onClick={() => { void onDelete(); }}
            >
              <Icon tokens={trashTokens} />
            </Button>
          </Tooltip>
        </Row>
      </Row>
      {hasDeleteError && (
        <Text color="error" data-testid={`chat-history-delete-error-${session.sessionId}`} type="b200">
          {t('chat_history_delete_error')}
        </Text>
      )}
      {editErrorMessageKey && (
        <Text color="error" data-testid={`chat-history-edit-error-${session.sessionId}`} type="b200">
          {t(
            editErrorMessageKey,
            editErrorMessageKey === 'chat_history_title_too_long'
              ? { max: SESSION_TITLE_MAX_LENGTH }
              : undefined,
          )}
        </Text>
      )}
    </div>
  );
};
